import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Coins, Play, RefreshCw, Search, TrendingUp, Users, Zap } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

const COLOR_MODES = ["parity", "sapre", "bcone", "emerd"];
const COLOR_HISTORY_MODES = ["all", ...COLOR_MODES];

function AdminCasino() {
  const today = new Date().toISOString().slice(0, 10);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = getValidTab(searchParams.get("tab"));
  const [tab, setTab] = useState(initialTab);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [preset, setPreset] = useState("today");
  const [query, setQuery] = useState("");
  const [overview, setOverview] = useState({ brand_name: "MK Casino", logo_url: "/logo5.png", summary: {}, games: [] });
  const [aviatorRows, setAviatorRows] = useState([]);
  const [colorRows, setColorRows] = useState([]);
  const [colorAnalytics, setColorAnalytics] = useState({ total_bets: 0, total_stake: 0, total_payout: 0, summary_by_mode: [] });
  const [colorRounds, setColorRounds] = useState([]);
  const [colorUpcoming, setColorUpcoming] = useState([]);
  const [selectedColorMode, setSelectedColorMode] = useState("parity");
  const [colorHistoryMode, setColorHistoryMode] = useState("all");
  const [colorSettings, setColorSettings] = useState({ red_green: "1.95", violet: "4.5", number: "9", size: "2", zero_red: "1.5", five_green: "1.5" });
  const [roundMeta, setRoundMeta] = useState({ round_ms: 60000, lock_ms: 15000, result_mode: "auto" });
  const [colorPage, setColorPage] = useState(1);
  const [colorPagination, setColorPagination] = useState({ page: 1, page_size: 30, total: 0, total_pages: 1 });
  const [actionMessage, setActionMessage] = useState("");
  const [clockNow, setClockNow] = useState(() => Date.now());

  const switchTab = (nextTab) => {
    const safeTab = getValidTab(nextTab);
    setTab(safeTab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", safeTab);
    setSearchParams(nextParams, { replace: true });
  };

  const loadData = useCallback(async () => {
    const params = new URLSearchParams();
    if (preset !== "all" && dateFrom && dateTo) {
      params.set("date_from", dateFrom);
      params.set("date_to", dateTo);
    }
    if (query.trim()) {
      params.set("query", query.trim());
    }
    const colorParams = new URLSearchParams(params);
    if (colorHistoryMode !== "all") {
      colorParams.set("mode", colorHistoryMode);
    }
    colorParams.set("page", String(colorPage));
    colorParams.set("page_size", "30");
    const [overviewRes, aviatorRes, colorRes, roundsRes] = await Promise.all([
      API.get(`/admin/casino/overview${params.toString() ? `?${params.toString()}` : ""}`),
      API.get(`/admin/casino/aviator${params.toString() ? `?${params.toString()}` : ""}`),
      API.get(`/admin/casino/color?${colorParams.toString()}`),
      API.get("/admin/color/rounds"),
    ]);
    setOverview(overviewRes.data || { brand_name: "MK Casino", logo_url: "/logo5.png", summary: {}, games: [] });
    setAviatorRows(Array.isArray(aviatorRes.data) ? aviatorRes.data : []);
    setColorRows(Array.isArray(colorRes.data) ? colorRes.data : Array.isArray(colorRes.data?.rows) ? colorRes.data.rows : []);
    setColorAnalytics(colorRes.data?.analytics || { total_bets: 0, total_stake: 0, total_payout: 0, summary_by_mode: [] });
    setColorPagination(colorRes.data?.pagination || { page: 1, page_size: 30, total: 0, total_pages: 1 });

    const payload = roundsRes.data || {};
    const rounds = Array.isArray(payload) ? payload : Array.isArray(payload.rounds) ? payload.rounds : [];
    setColorRounds(rounds);
    setColorUpcoming(Array.isArray(payload.upcoming) ? payload.upcoming : []);
    setRoundMeta({
      round_ms: Number(payload?.settings?.round_ms || 60000),
      lock_ms: Number(payload?.settings?.lock_ms || 15000),
      result_mode: String(payload?.settings?.result_mode || "auto"),
    });
    if (payload?.settings?.payouts) {
      setColorSettings({
        red_green: String(payload.settings.payouts.red_green ?? "1.95"),
        violet: String(payload.settings.payouts.violet ?? "4.5"),
        number: String(payload.settings.payouts.number ?? "9"),
        size: String(payload.settings.payouts.size ?? "2"),
        zero_red: String(payload.settings.payouts.zero_red ?? "1.5"),
        five_green: String(payload.settings.payouts.five_green ?? "1.5"),
      });
    }
  }, [colorHistoryMode, colorPage, dateFrom, dateTo, preset, query]);

  useEffect(() => {
    if (preset === "today") {
      const next = new Date().toISOString().slice(0, 10);
      setDateFrom(next);
      setDateTo(next);
    }
  }, [preset]);

  useEffect(() => {
    loadData().catch(() => {});
  }, [loadData]);

  useEffect(() => {
    const urlTab = getValidTab(searchParams.get("tab"));
    if (urlTab !== tab) setTab(urlTab);
  }, [searchParams, tab]);

  useEffect(() => {
    setColorPage(1);
  }, [colorHistoryMode]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const gameCards = useMemo(() => overview.games || [], [overview.games]);
  const modeRounds = useMemo(
    () => COLOR_MODES.reduce((acc, mode) => {
      acc[mode] = colorRounds.filter((round) => String(round.mode || "").toLowerCase() === mode);
      return acc;
    }, {}),
    [colorRounds]
  );
  const filteredColorRows = colorRows;
  const currentModeUpcoming = useMemo(
    () => colorUpcoming.filter((item) => item.mode === selectedColorMode).slice(0, 10),
    [colorUpcoming, selectedColorMode]
  );
  const currentColorRound = useMemo(() => {
    const rounds = modeRounds[selectedColorMode] || [];
    return rounds.find((round) => String(round.status || "").toLowerCase() !== "declared") || rounds[0] || null;
  }, [modeRounds, selectedColorMode]);
  const currentExposure = currentColorRound?.exposure || { total_bets: 0, total_stake: 0, numbers: [], colors: {}, sizes: {} };
  const currentRoundRemainingMs = Math.max(0, new Date(currentColorRound?.round_end_at || 0).getTime() - clockNow);
  const colorCountdown = currentColorRound?.round_end_at ? formatCountdown(currentRoundRemainingMs) : "--:--";

  const saveColorSettings = async () => {
    setActionMessage("");
    try {
      await API.post("/admin/color/settings", { ...colorSettings, result_mode: roundMeta.result_mode });
      setActionMessage("Color payout settings updated.");
      await loadData();
    } catch (err) {
      setActionMessage(err.response?.data?.message || "Unable to save payout settings.");
    }
  };

  const saveScheduledResult = async (period, resultNumber, enabled = true) => {
    setActionMessage("");
    try {
      await API.post("/admin/color/schedule", {
        mode: selectedColorMode,
        period,
        result_number: Number(resultNumber),
        enabled,
      });
      setActionMessage(`Scheduled result saved for ${period}.`);
      await loadData();
    } catch (err) {
      setActionMessage(err.response?.data?.message || "Unable to save scheduled result.");
    }
  };

  const declareCurrentRoundNumber = async (number) => {
    if (!currentColorRound?.id || String(currentColorRound.status || "").toLowerCase() === "declared") return;
    setActionMessage("");
    try {
      await API.post("/admin/color/result", {
        round_id: Number(currentColorRound.id),
        result_number: Number(number),
      });
      setActionMessage(`Result ${number} declared for ${selectedColorMode.toUpperCase()} ${currentColorRound.period}.`);
      await loadData();
    } catch (err) {
      setActionMessage(err.response?.data?.message || "Unable to declare result.");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div style={brandWrap}>
            <img src={overview.logo_url || "/logo5.png"} alt="MK Casino" style={logo} />
            <div>
              <h1 style={title}>MK Casino</h1>
              <p style={subtitle}>Casino bets, Aviator and Color Prediction control</p>
            </div>
          </div>
          <button type="button" style={refreshButton} onClick={() => loadData().catch(() => {})}>
            <RefreshCw size={16} />
          </button>
        </div>

        <div style={toolbar}>
          <div style={presetRow}>
            <button type="button" style={preset === "all" ? activeChip : chip} onClick={() => setPreset("all")}>All</button>
            <button type="button" style={preset === "today" ? activeChip : chip} onClick={() => setPreset("today")}>Today</button>
          </div>
          <div style={dateRow}>
            <label style={dateWrap}>
              <CalendarDays size={14} />
              <input type="date" value={dateFrom} onChange={(e) => { setPreset("custom"); setDateFrom(e.target.value); }} style={dateInput} />
            </label>
            <span style={toText}>to</span>
            <label style={dateWrap}>
              <CalendarDays size={14} />
              <input type="date" value={dateTo} onChange={(e) => { setPreset("custom"); setDateTo(e.target.value); }} style={dateInput} />
            </label>
          </div>
          <div style={searchBox}>
            <Search size={14} color="#64748b" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search user, phone, round..." style={searchInput} />
          </div>
        </div>

        <div style={summaryGrid}>
          <SummaryCard icon={TrendingUp} label="Total Bets" value={formatCount(overview.summary?.total_bets)} tone="linear-gradient(135deg,#2563eb,#4f46e5)" />
          <SummaryCard icon={Coins} label="Total Stake" value={formatMoney(overview.summary?.total_stake)} tone="linear-gradient(135deg,#22c55e,#16a34a)" />
          <SummaryCard icon={Zap} label="Total Payout" value={formatMoney(overview.summary?.total_payout)} tone="linear-gradient(135deg,#f97316,#ea580c)" />
          <SummaryCard icon={Users} label="Players" value={formatCount(overview.summary?.unique_players)} tone="linear-gradient(135deg,#a855f7,#db2777)" />
        </div>

        <div style={tabRow}>
          <button type="button" style={tab === "overview" ? activeTab : tabButton} onClick={() => switchTab("overview")}>Overview</button>
          <button type="button" style={tab === "aviator" ? activeTab : tabButton} onClick={() => switchTab("aviator")}>Aviator</button>
          <button type="button" style={tab === "color" ? activeTab : tabButton} onClick={() => switchTab("color")}>Color Prediction</button>
        </div>

        {tab === "overview" ? (
          <div style={gameGrid}>
            {gameCards.map((game) => (
              <article key={game.key} style={gameCard}>
                <div style={gameHead}>
                  <span style={gameIcon}><Play size={16} /></span>
                  <div>
                    <strong style={gameTitle}>{game.title}</strong>
                    <div style={gameCode}>{game.code}</div>
                  </div>
                </div>
                <div style={gameStats}>
                  <StatLine label="Bet Amount" value={formatMoney(game.total_stake)} />
                  <StatLine label="Winnings" value={formatMoney(game.total_payout)} />
                  <StatLine label="GGR" value={formatMoney(game.ggr)} />
                  <StatLine label="Win Rate" value={`${Number(game.win_rate || 0).toFixed(1)}%`} />
                </div>
                <div style={gameFoot}>{`${formatCount(game.unique_players)} players • ${formatCount(game.total_bets)} bets`}</div>
              </article>
            ))}
            {!gameCards.length && <div style={emptyCard}>No casino game data found.</div>}
          </div>
        ) : tab === "aviator" ? (
          <section style={tableCard}>
            <div style={tableHead}>Aviator Transactions</div>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>SN</th>
                  <th style={th}>User</th>
                  <th style={th}>Type</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Status</th>
                  <th style={th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {aviatorRows.map((row) => (
                  <tr key={row.id}>
                    <td style={td}>{row.id}</td>
                    <td style={td}>
                      <AdminUserLink userId={row.user_id} name={row.user_name || row.user_phone} phone={row.user_phone && row.user_name ? row.user_phone : ""} />
                    </td>
                    <td style={td}><span style={typePill(Number(row.payout || 0) > 0 ? "credit" : "debit")}>{Number(row.payout || 0) > 0 ? "CREDIT" : "DEBIT"}</span></td>
                    <td style={{ ...td, color: Number(row.payout || 0) > 0 ? "#16a34a" : "#dc2626", fontWeight: 800 }}>
                      {Number(row.payout || 0) > 0 ? `+${formatMoney(row.payout)}` : `-${formatMoney(row.amount)}`}
                    </td>
                    <td style={td}><span style={statusPill(row.status)}>{String(row.status || "").toUpperCase()}</span></td>
                    <td style={td}>{formatDateTime(row.settled_at || row.created_at)}</td>
                  </tr>
                ))}
                {!aviatorRows.length && (
                  <tr>
                    <td colSpan={6} style={emptyCell}>No Aviator records for this date range.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        ) : (
          <div style={colorLayout}>
            <section style={heroCard}>
              <div style={colorControlHeader}>
                <div>
                  <div style={heroBadge}>Color Prediction Result Control</div>
                  <h2 style={heroTitle}>{selectedColorMode.toUpperCase()} Current Round</h2>
                </div>
                <div style={roundStatsBox}>
                  <strong>{currentColorRound?.period || "No round"}</strong>
                  <span>{String(currentColorRound?.status || "-").toUpperCase()}</span>
                  <strong>{formatMoney(currentExposure.total_stake)}</strong>
                  <span>{formatCount(currentExposure.total_bets)} bets</span>
                </div>
              </div>
              <div style={modeFilterRow}>
                {COLOR_MODES.map((mode) => (
                  <button key={mode} type="button" style={selectedColorMode === mode ? activeHeroModeTab : heroModeTab} onClick={() => setSelectedColorMode(mode)}>
                    {mode.toUpperCase()}
                  </button>
                ))}
              </div>
              <div style={resultCountdownBar}>
                <span>Result countdown</span>
                <strong>{colorCountdown}</strong>
                <small>{roundMeta.result_mode === "manual" ? "Manual result mode" : "Auto result mode"}</small>
              </div>
              <div style={colorExposureGrid}>
                {(currentExposure.numbers || []).map((item) => (
                  <button
                    key={item.number}
                    type="button"
                    style={numberResultButton(item.number, currentColorRound?.status)}
                    onClick={() => declareCurrentRoundNumber(item.number)}
                    disabled={!currentColorRound?.id || String(currentColorRound?.status || "").toLowerCase() === "declared"}
                    title={`Declare ${item.number} for ${selectedColorMode.toUpperCase()}`}
                  >
                    <span style={numberBadge}>{item.number}</span>
                    <span style={numberColorText}>{item.colors.join(" + ").toUpperCase()}</span>
                    <span style={numberAmount}>{formatMoney(item.stake)}</span>
                    <span style={numberMeta}>{formatCount(item.bets)} no. bets</span>
                    <span style={resultExposure}>Total exposure {formatMoney(item.result_stake)}</span>
                  </button>
                ))}
              </div>
              <div style={colorSummaryGrid}>
                {["green", "red", "violet"].map((color) => (
                  <div key={color} style={colorStatCard(color)}>
                    <strong>{color.toUpperCase()}</strong>
                    <span>{formatMoney(currentExposure.colors?.[color]?.stake)}</span>
                    <small>{formatCount(currentExposure.colors?.[color]?.bets)} bets</small>
                  </div>
                ))}
                {["small", "big"].map((size) => (
                  <div key={size} style={sizeStatCard}>
                    <strong>{size.toUpperCase()}</strong>
                    <span>{formatMoney(currentExposure.sizes?.[size]?.stake)}</span>
                    <small>{formatCount(currentExposure.sizes?.[size]?.bets)} bets</small>
                  </div>
                ))}
              </div>
              {actionMessage && <div style={heroActionNote}>{actionMessage}</div>}
            </section>

            <section style={settingsCard}>
              <div style={cardHeaderRow}>
                <div>
                  <h2 style={cardTitle}>Payout Settings</h2>
                  <p style={cardText}>Admin can change rates later without changing the code again.</p>
                </div>
              </div>
              <div style={settingsGrid}>
                <label style={formLabel}>Mode<select value={roundMeta.result_mode} onChange={(e) => setRoundMeta((current) => ({ ...current, result_mode: e.target.value }))} style={selectInput}><option value="auto">Auto</option><option value="manual">Manual</option></select></label>
                <label style={formLabel}>Red / Green<input value={colorSettings.red_green} onChange={(e) => setColorSettings((current) => ({ ...current, red_green: e.target.value }))} style={textInput} /></label>
                <label style={formLabel}>Violet<input value={colorSettings.violet} onChange={(e) => setColorSettings((current) => ({ ...current, violet: e.target.value }))} style={textInput} /></label>
                <label style={formLabel}>Single Number<input value={colorSettings.number} onChange={(e) => setColorSettings((current) => ({ ...current, number: e.target.value }))} style={textInput} /></label>
                <label style={formLabel}>Big / Small<input value={colorSettings.size} onChange={(e) => setColorSettings((current) => ({ ...current, size: e.target.value }))} style={textInput} /></label>
                <label style={formLabel}>Red on 0<input value={colorSettings.zero_red} onChange={(e) => setColorSettings((current) => ({ ...current, zero_red: e.target.value }))} style={textInput} /></label>
                <label style={formLabel}>Green on 5<input value={colorSettings.five_green} onChange={(e) => setColorSettings((current) => ({ ...current, five_green: e.target.value }))} style={textInput} /></label>
              </div>
              <button type="button" style={declareButton} onClick={() => saveColorSettings()}>Save Payout Rates</button>
              {actionMessage && <div style={actionNote}>{actionMessage}</div>}
            </section>

            <section style={settingsCard}>
              <div style={cardHeaderRow}>
                <div>
                  <h2 style={cardTitle}>Next 10 Planned Rounds</h2>
                  <p style={cardText}>Schedule results for upcoming rounds in {selectedColorMode.toUpperCase()} mode.</p>
                </div>
              </div>
              <div style={scheduleGrid}>
                {currentModeUpcoming.map((item) => (
                  <div key={item.period} style={scheduleCard}>
                    <strong>{item.period}</strong>
                    <span style={mutedInfo}>{item.round_start_at}</span>
                    <div style={scheduleControls}>
                      <select defaultValue={item.planned_result_number ?? 0} onChange={(e) => saveScheduledResult(item.period, e.target.value, true)} style={selectInput}>
                        {Array.from({ length: 10 }, (_, index) => <option key={index} value={index}>{index}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={tableCard}>
              <div style={tableHeadWrap}>
                <div style={tableHead}>Color Transactions</div>
                <div style={modeFilterRow}>
                  {COLOR_HISTORY_MODES.map((mode) => (
                    <button key={mode} type="button" style={colorHistoryMode === mode ? activeMiniTab : miniTab} onClick={() => setColorHistoryMode(mode)}>
                      {mode === "all" ? "ALL BETS" : mode.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div style={analyticsGrid}>
                {(colorAnalytics.summary_by_mode || []).map((item) => (
                  <div key={item.mode} style={analyticsCard(colorHistoryMode !== "all" && item.mode === colorHistoryMode)}>
                    <strong>{item.mode.toUpperCase()}</strong>
                    <span>{item.bets} bets</span>
                    <span>{formatMoney(item.stake)} stake</span>
                    <span>{formatMoney(item.payout)} payout</span>
                  </div>
                ))}
              </div>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>SN</th>
                    <th style={th}>User</th>
                    <th style={th}>Mode</th>
                    <th style={th}>Period</th>
                    <th style={th}>Bet</th>
                    <th style={th}>Amount</th>
                    <th style={th}>Balance</th>
                    <th style={th}>Result</th>
                    <th style={th}>Status</th>
                    <th style={th}>Win</th>
                    <th style={th}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColorRows.map((row) => (
                    <tr key={row.id}>
                      <td style={td}>{row.id}</td>
                      <td style={td}>
                        <AdminUserLink userId={row.user_id} name={row.user_name || row.user_phone} phone={row.user_phone && row.user_name ? row.user_phone : ""} />
                      </td>
                      <td style={td}><span style={modePill}>{getColorModeFromPeriod(row.period).toUpperCase()}</span></td>
                      <td style={td}>{row.period}</td>
                      <td style={td}>
                        <div>{String(row.bet_type).toUpperCase()} · {String(row.selection).toUpperCase()}</div>
                      </td>
                      <td style={amountCell}>{formatMoney(row.amount)}</td>
                      <td style={td}>{row.wallet_balance === null || row.wallet_balance === undefined ? "-" : formatMoney(row.wallet_balance)}</td>
                      <td style={td}>{row.result_number === null || row.result_number === undefined ? "-" : `${row.result_number} (${row.result_color})`}</td>
                      <td style={td}><span style={statusPill(row.status)}>{String(row.status || "").toUpperCase()}</span></td>
                      <td style={{ ...td, color: Number(row.payout || 0) > 0 ? "#16a34a" : "#64748b", fontWeight: 900 }}>{formatMoney(row.payout)}</td>
                      <td style={td}>{formatDateTime(row.created_at)}</td>
                    </tr>
                  ))}
                  {!filteredColorRows.length && (
                    <tr>
                      <td colSpan={11} style={emptyCell}>No Color Game records for this mode and date range.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div style={paginationRow}>
                <button type="button" style={miniTab} disabled={colorPagination.page <= 1} onClick={() => setColorPage((value) => Math.max(1, value - 1))}>Prev</button>
                <span style={pageMeta}>Page {colorPagination.page} / {colorPagination.total_pages}</span>
                <button type="button" style={miniTab} disabled={colorPagination.page >= colorPagination.total_pages} onClick={() => setColorPage((value) => Math.min(colorPagination.total_pages, value + 1))}>Next</button>
              </div>
            </section>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function getValidTab(value) {
  return ["overview", "aviator", "color"].includes(value) ? value : "overview";
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  return (
    <article style={{ ...summaryCard, background: tone }}>
      <div style={summaryTop}>
        <span style={summaryIcon}><Icon size={18} /></span>
        <span>{label}</span>
      </div>
      <strong style={summaryValue}>{value}</strong>
    </article>
  );
}

function StatLine({ label, value }) {
  return (
    <div style={statLine}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatCount(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function formatCountdown(msValue) {
  const seconds = Math.max(0, Math.ceil(Number(msValue || 0) / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function getColorModeFromPeriod(period = "") {
  const mode = String(period || "").split("-")[0]?.toLowerCase();
  return COLOR_MODES.includes(mode) ? mode : "-";
}

const typePill = (type) => ({
  display: "inline-block",
  padding: "5px 10px",
  borderRadius: "999px",
  background: type === "credit" ? "#dcfce7" : "#fee2e2",
  color: type === "credit" ? "#15803d" : "#dc2626",
  fontWeight: 800,
  fontSize: "11px",
});

const statusPill = (status) => ({
  display: "inline-block",
  padding: "5px 10px",
  borderRadius: "999px",
  background: String(status || "").toLowerCase() === "completed" || String(status || "").toLowerCase() === "won" ? "#dcfce7" : "#f1f5f9",
  color: String(status || "").toLowerCase() === "completed" || String(status || "").toLowerCase() === "won" ? "#15803d" : "#475569",
  fontWeight: 800,
  fontSize: "11px",
});

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" };
const brandWrap = { display: "flex", alignItems: "center", gap: "14px" };
const logo = { width: "54px", height: "54px", borderRadius: "18px", objectFit: "cover", background: "#fff", border: "1px solid #e5e7eb" };
const title = { margin: 0, fontSize: "24px", fontWeight: 900 };
const subtitle = { margin: "4px 0 0", color: "#64748b" };
const refreshButton = { width: "42px", height: "42px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const toolbar = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" };
const presetRow = { display: "flex", gap: "8px" };
const chip = { height: "36px", padding: "0 12px", border: "1px solid #dbe4f0", borderRadius: "10px", background: "#fff", color: "#334155", fontWeight: 700, cursor: "pointer" };
const activeChip = { ...chip, background: "#316ce8", color: "#fff", borderColor: "#316ce8" };
const dateRow = { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" };
const toText = { color: "#64748b", fontWeight: 700 };
const dateWrap = { display: "flex", alignItems: "center", gap: "8px", height: "40px", border: "1px solid #dbe4f0", borderRadius: "10px", padding: "0 12px", background: "#fff" };
const dateInput = { border: 0, outline: 0, background: "transparent", fontWeight: 700 };
const searchBox = { width: "280px", maxWidth: "100%", height: "40px", border: "1px solid #dbe4f0", borderRadius: "10px", padding: "0 12px", background: "#fff", display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", alignItems: "center", gap: "8px" };
const searchInput = { border: 0, outline: 0, width: "100%", background: "transparent" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" };
const summaryCard = { minHeight: "118px", color: "#fff", borderRadius: "20px", padding: "18px", display: "grid", gap: "18px" };
const summaryTop = { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", fontWeight: 800 };
const summaryIcon = { width: "38px", height: "38px", borderRadius: "12px", background: "rgba(255,255,255,0.16)", display: "grid", placeItems: "center" };
const summaryValue = { fontSize: "30px", fontWeight: 900 };
const tabRow = { display: "flex", gap: "10px", flexWrap: "wrap" };
const tabButton = { height: "40px", padding: "0 16px", border: "1px solid #dbe4f0", borderRadius: "10px", background: "#fff", fontWeight: 700, cursor: "pointer" };
const activeTab = { ...tabButton, background: "#eff6ff", color: "#2563eb", borderColor: "#bfdbfe" };
const gameGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" };
const gameCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "20px", display: "grid", gap: "18px" };
const gameHead = { display: "flex", alignItems: "center", gap: "12px" };
const gameIcon = { width: "44px", height: "44px", display: "grid", placeItems: "center", background: "#eff6ff", color: "#2563eb", borderRadius: "14px" };
const gameTitle = { display: "block", fontSize: "22px", color: "#0f172a" };
const gameCode = { marginTop: "4px", color: "#64748b", fontSize: "12px", fontWeight: 700 };
const gameStats = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };
const statLine = { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "12px", padding: "12px", display: "grid", gap: "8px", color: "#64748b", fontSize: "12px" };
const gameFoot = { color: "#7c3aed", fontSize: "13px", fontWeight: 700 };
const emptyCard = { minHeight: "180px", display: "grid", placeItems: "center", background: "#fff", border: "1px dashed #cbd5e1", borderRadius: "18px", color: "#94a3b8" };
const colorLayout = { display: "grid", gap: "16px" };
const heroCard = { background: "linear-gradient(135deg,#0f172a,#14532d)", borderRadius: "18px", padding: "16px", color: "#fff", display: "grid", gap: "10px", boxShadow: "0 12px 26px rgba(15,23,42,.16)" };
const heroBadge = { display: "inline-flex", width: "fit-content", padding: "5px 10px", borderRadius: "999px", background: "rgba(255,255,255,.14)", fontWeight: 800, fontSize: "11px" };
const heroTitle = { margin: "4px 0 0", fontSize: "22px", lineHeight: 1.1, fontWeight: 900 };
const colorControlHeader = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: "16px", flexWrap: "wrap" };
const roundStatsBox = { minWidth: "170px", display: "grid", gap: "3px", padding: "10px 12px", borderRadius: "14px", background: "rgba(255,255,255,.13)", border: "1px solid rgba(255,255,255,.2)", color: "#fff" };
const heroModeTab = { height: "36px", padding: "0 14px", border: "1px solid rgba(255,255,255,.28)", borderRadius: "999px", background: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 900, cursor: "pointer" };
const activeHeroModeTab = { ...heroModeTab, background: "#fff", color: "#14532d", borderColor: "#fff" };
const resultCountdownBar = { minHeight: "52px", borderRadius: "14px", background: "rgba(255,255,255,.13)", border: "1px solid rgba(255,255,255,.2)", display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", gap: "12px", padding: "8px 12px", fontWeight: 900 };
const colorExposureGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))", gap: "8px" };
const numberResultButton = (number, status) => {
  const color = number === 0 ? "#ef4444" : number === 5 ? "#22c55e" : number % 2 === 0 ? "#ef4444" : "#22c55e";
  const declared = String(status || "").toLowerCase() === "declared";
  return {
    minHeight: "108px",
    border: `1px solid ${declared ? "rgba(255,255,255,.12)" : "rgba(255,255,255,.28)"}`,
    borderRadius: "18px",
    padding: "12px",
    display: "grid",
    gap: "3px",
    justifyItems: "start",
    background: declared ? "rgba(255,255,255,.08)" : `linear-gradient(145deg, rgba(255,255,255,.16), ${color}44)`,
    color: "#fff",
    cursor: declared ? "not-allowed" : "pointer",
    opacity: declared ? 0.55 : 1,
    boxShadow: declared ? "none" : `0 12px 24px ${color}22`,
  };
};
const numberBadge = { width: "34px", height: "34px", display: "grid", placeItems: "center", borderRadius: "12px", background: "rgba(255,255,255,.18)", fontSize: "20px", fontWeight: 1000 };
const numberColorText = { fontSize: "10px", fontWeight: 900, color: "rgba(255,255,255,.72)" };
const numberAmount = { fontSize: "15px", fontWeight: 1000, lineHeight: 1.1, overflowWrap: "anywhere" };
const numberMeta = { fontSize: "11px", color: "rgba(255,255,255,.72)", fontWeight: 800 };
const resultExposure = { marginTop: "2px", fontSize: "10px", color: "#fde68a", fontWeight: 900 };
const colorSummaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px" };
const colorStatCard = (color) => {
  const tones = { green: "#16a34a", red: "#dc2626", violet: "#a855f7" };
  return { display: "grid", gap: "3px", padding: "12px", borderRadius: "16px", background: `${tones[color] || "#334155"}44`, border: `1px solid ${tones[color] || "#334155"}`, color: "#fff" };
};
const sizeStatCard = { display: "grid", gap: "3px", padding: "12px", borderRadius: "16px", background: "rgba(59,130,246,.24)", border: "1px solid rgba(147,197,253,.6)", color: "#fff" };
const heroActionNote = { padding: "10px 12px", borderRadius: "14px", background: "rgba(34,197,94,.22)", border: "1px solid rgba(134,239,172,.45)", color: "#dcfce7", fontWeight: 900 };
const settingsCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "20px", display: "grid", gap: "16px" };
const cardHeaderRow = { display: "flex", justifyContent: "space-between", alignItems: "start", gap: "14px", flexWrap: "wrap" };
const cardTitle = { margin: 0, fontSize: "22px", color: "#0f172a", fontWeight: 900 };
const cardText = { margin: "6px 0 0", color: "#64748b" };
const settingsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" };
const scheduleGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" };
const scheduleCard = { borderRadius: "16px", border: "1px solid #e2e8f0", background: "#f8fafc", padding: "12px", display: "grid", gap: "8px" };
const scheduleControls = { display: "grid", gap: "8px" };
const formLabel = { display: "grid", gap: "7px", color: "#475569", fontWeight: 800, fontSize: "13px" };
const selectInput = { height: "42px", border: "1px solid #dbe4f0", borderRadius: "10px", padding: "0 12px", fontWeight: 800, background: "#fff" };
const textInput = { height: "42px", border: "1px solid #dbe4f0", borderRadius: "10px", padding: "0 12px", fontWeight: 800, background: "#fff" };
const declareButton = { height: "42px", border: 0, borderRadius: "10px", background: "linear-gradient(135deg,#22c55e,#15803d)", color: "#fff", fontWeight: 900, cursor: "pointer" };
const actionNote = { padding: "10px 12px", borderRadius: "12px", background: "#eff6ff", color: "#1d4ed8", fontWeight: 800 };
const mutedInfo = { color: "#64748b", fontSize: "12px" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", overflow: "hidden" };
const tableHeadWrap = { display: "grid", gap: "8px", padding: "18px 18px 0" };
const tableHead = { padding: "0 0 6px", fontSize: "22px", fontWeight: 900, color: "#0f172a" };
const modeFilterRow = { display: "flex", gap: "8px", flexWrap: "wrap", paddingBottom: "12px" };
const miniTab = { height: "34px", padding: "0 12px", border: "1px solid #dbe4f0", borderRadius: "999px", background: "#fff", color: "#334155", fontWeight: 800, cursor: "pointer" };
const activeMiniTab = { ...miniTab, background: "#0f172a", color: "#fff", borderColor: "#0f172a" };
const analyticsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", padding: "0 18px 16px" };
const analyticsCard = (active) => ({ display: "grid", gap: "4px", padding: "12px", borderRadius: "14px", border: `1px solid ${active ? "#bfdbfe" : "#e2e8f0"}`, background: active ? "#eff6ff" : "#f8fafc", color: "#334155", fontSize: "12px" });
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "14px 18px", fontSize: "12px", color: "#64748b", textTransform: "uppercase", borderBottom: "1px solid #f1f5f9" };
const td = { padding: "16px 18px", fontSize: "14px", color: "#0f172a", borderBottom: "1px solid #f8fafc" };
const amountCell = { ...td, color: "#0f766e", fontWeight: 900, whiteSpace: "nowrap" };
const modePill = { display: "inline-flex", alignItems: "center", minHeight: "24px", padding: "0 8px", borderRadius: "999px", background: "#eff6ff", color: "#1d4ed8", fontSize: "11px", fontWeight: 900 };
const emptyCell = { padding: "28px", textAlign: "center", color: "#94a3b8" };
const paginationRow = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "14px 18px 18px" };
const pageMeta = { color: "#64748b", fontWeight: 800 };

export default AdminCasino;
